import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { CalendarClock, Settings2, Loader2, Flame, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

function tone(days) {
  if (days == null) return "text-muted-foreground";
  if (days < 0) return "text-destructive";
  if (days <= 30) return "text-amber-500";
  return "text-accent";
}

const TRACKS = ["CQC", "Ofsted", "Fostering", "Aesthetics", "Healthcare"];
export const TRACK_LABELS = { CQC: "CQC", Ofsted: "Ofsted", Aesthetics: "CQC · Aesthetics clinic", Healthcare: "CQC · GP & hospitals", Fostering: "Ofsted · Fostering agency" };

const daysLabel = (days) => {
  if (days < 0) return "days overdue — expect a visit any time";
  if (days === 0) return "today — good luck!";
  return "days to go";
};

export default function InspectionCountdown() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ regulator: "CQC", next_inspection_date: "", last_inspection_date: "", reminders_enabled: true, digest_enabled: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/me/inspection").then((r) => setData(r.data)); }, []);

  const openSettings = () => {
    setForm({
      regulator: data?.regulator || "CQC",
      next_inspection_date: data?.next_inspection_date || "",
      last_inspection_date: data?.last_inspection_date || "",
      reminders_enabled: data?.reminders_enabled !== false,
      digest_enabled: data?.digest_enabled !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/me/inspection", form);
      setData(d); setOpen(false);
      toast.success("Inspection details saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggle = async (item, done) => {
    setData((d) => ({ ...d, checklist: d.checklist.map((c) => (c.id === item.id ? { ...c, done } : c)),
      completed_count: d.completed_count + (done ? 1 : -1) }));
    try {
      const { data: d } = await api.post("/me/inspection/checklist", { item_id: item.id, done });
      setData(d);
      if (d.completed_count === d.total_count) toast.success("Weekly readiness checklist complete!");
    } catch { toast.error("Could not update checklist"); }
  };

  if (!data) return <div className="bg-card border border-border rounded-2xl p-6 shadow-sm flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const pct = data.total_count ? Math.round((data.completed_count / data.total_count) * 100) : 0;
  const days = data.days_left;

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden" data-testid="inspection-countdown-card">
      <div className="grid md:grid-cols-5">
        <div className="md:col-span-2 p-6 bg-primary text-primary-foreground relative overflow-hidden">
          <div className="absolute -right-8 -bottom-8 w-40 h-40 rounded-full bg-white/10" />
          <div className="flex items-center justify-between mb-4 relative">
            <span className="text-xs font-bold uppercase tracking-[0.15em] opacity-80 flex items-center gap-1.5">
              <CalendarClock className="w-4 h-4" /> Next inspection
            </span>
            <Badge className="bg-white/15 text-white border-0">{TRACK_LABELS[data.regulator] || data.regulator}</Badge>
          </div>
          {days == null ? (
            <div className="relative">
              <p className="font-display text-2xl font-bold mb-1">No date set</p>
              <p className="text-sm opacity-80 mb-4">Add your expected inspection window to start the countdown.</p>
            </div>
          ) : (
            <div className="relative">
              <p className="font-display text-6xl font-extrabold leading-none" data-testid="inspection-days-left">{Math.abs(days)}</p>
              <p className="text-sm opacity-90 mt-1">
                {daysLabel(days)}
              </p>
              <p className="text-xs opacity-70 mt-3">
                {new Date(data.next_inspection_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
          )}
          <Button size="sm" variant="secondary" className="mt-4 relative" onClick={openSettings} data-testid="inspection-settings-button">
            <Settings2 className="w-4 h-4 mr-1.5" /> {days == null ? "Set inspection date" : "Edit"}
          </Button>
        </div>

        <div className="md:col-span-3 p-6">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-primary" /> This week's readiness checklist
              </h3>
              <p className="text-xs text-muted-foreground">Resets every Monday · week {data.week_key}</p>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="inspection-weeks-completed">
              <Flame className={`w-4 h-4 ${data.weeks_completed > 0 ? "text-amber-500" : ""}`} /> {data.weeks_completed} full week{data.weeks_completed === 1 ? "" : "s"}
            </div>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <Progress value={pct} className="h-2 flex-1" />
            <span className={`text-sm font-semibold ${tone(days)}`} data-testid="inspection-progress-label">{data.completed_count}/{data.total_count}</span>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2">
            {data.checklist.map((item) => (
              <li key={item.id}>
                <label className={`flex items-start gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-colors ${item.done ? "bg-accent/5 border-accent/30" : "border-border hover:border-primary/40"}`}
                  data-testid={`checklist-item-${item.id}`}>
                  <Checkbox checked={item.done} onCheckedChange={(v) => toggle(item, !!v)} className="mt-0.5" data-testid={`checklist-checkbox-${item.id}`} />
                  <span className="min-w-0">
                    <span className={`block text-sm leading-snug ${item.done ? "line-through text-muted-foreground" : ""}`}>{item.label}</span>
                    <span className="text-[11px] text-muted-foreground">{item.kq}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="inspection-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Inspection details</DialogTitle>
            <DialogDescription>Set your service type and expected inspection date. The weekly checklist, training and readiness score adapt to it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Regulator</Label>
              <div className="flex gap-2 mt-1.5">
                {TRACKS.map((r) => (
                  <button key={r} type="button" onClick={() => setForm({ ...form, regulator: r })} data-testid={`inspection-regulator-${r.toLowerCase()}`}
                    className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${form.regulator === r ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
                    {TRACK_LABELS[r]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="next-date">Expected next inspection</Label>
              <Input id="next-date" type="date" value={form.next_inspection_date} className="mt-1.5" data-testid="inspection-next-date-input"
                onChange={(e) => setForm({ ...form, next_inspection_date: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="last-date">Last inspection (optional)</Label>
              <Input id="last-date" type="date" value={form.last_inspection_date} className="mt-1.5" data-testid="inspection-last-date-input"
                onChange={(e) => setForm({ ...form, last_inspection_date: e.target.value })} />
            </div>
            <label className="flex items-center justify-between rounded-lg border border-border p-3 gap-3">
              <span>
                <span className="text-sm block">Weekly email reminder</span>
                <span className="text-xs text-muted-foreground">Every Friday when your inspection is within 60 days and the checklist is unfinished.</span>
              </span>
              <Switch checked={form.reminders_enabled} onCheckedChange={(v) => setForm({ ...form, reminders_enabled: v })} data-testid="inspection-reminders-switch" />
            </label>
            <label className="flex items-center justify-between rounded-lg border border-border p-3 gap-3">
              <span>
                <span className="text-sm block">Weekly regulatory digest</span>
                <span className="text-xs text-muted-foreground">Every Monday: new CQC or Ofsted publications for your service type.</span>
              </span>
              <Switch checked={form.digest_enabled} onCheckedChange={(v) => setForm({ ...form, digest_enabled: v })} data-testid="inspection-digest-switch" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="inspection-save-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
